import { inject, Injectable, signal } from '@angular/core';
import { App } from '@capacitor/app';
import { Geolocation } from '@capacitor/geolocation';
import { LaunchNavigator } from '@capgo/capacitor-launch-navigator';
import { Address, NativeGeocoder } from '@capgo/nativegeocoder';
import { TranslateService } from '@ngx-translate/core';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';

import { LatitudeLongitude, Location } from '@core/models/location';
import { environment } from '@environment/environment';

import { LogService } from '../log/log';
import { UiService } from '../ui/ui';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private readonly uiService = inject(UiService);
  private readonly logService = inject(LogService);
  private readonly translateService = inject(TranslateService);

  private readonly location$ = signal<Location | null>(null);
  readonly location = this.location$.asReadonly();
  private readonly locationAddress$ = signal<Address | null>(null);
  readonly locationAddress = this.locationAddress$.asReadonly();
  private readonly promptedForPermission$ = signal<boolean>(false);
  readonly promptedForPermission = this.promptedForPermission$.asReadonly();

  async getLocation(): Promise<Location | void> {
    this.logService.info('[LocationService] getLocation');
    try {
      const location = await Geolocation.getCurrentPosition();
      this.location$.set(location);
      await this.reverseGeocode({ latitude: location.coords.latitude, longitude: location.coords.longitude });
      return location;
    } catch (error) {
      this.logService.error('[LocationService] User Denied Location Permission', { error });
      if (!this.promptedForPermission()) {
        this.promptForLocationPermissions();
      }
    }
  }

  async promptForLocationPermissions() {
    this.logService.info('[LocationService] promptForLocationPermissions');
    this.promptedForPermission$.set(true);
    const resp = await this.uiService.showConfirmationModal({
      buttons: [
        {
          label: this.translateService.instant('Common.Cancel'),
          role: 'cancel',
          color: 'primary',
          fill: 'outline'
        },
        {
          label: this.translateService.instant('Common.Ok'),
          role: 'ok',
          color: 'primary'
        }
      ],
      title: this.translateService.instant('Location.Location_Permission'),
      message: this.translateService.instant('Location.Location_Warning')
    });
    if (resp == 'ok') {
      await this.addResumeEventListener();
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App
      });
    }
  }

  async reverseGeocode(coords: LatitudeLongitude): Promise<Address | null> {
    this.logService.info('[LocationService] reverseGeocode');
    try {
      const response = await NativeGeocoder.reverseGeocode({
        ...coords,
        apiKey: environment.mapApiKey
      });
      const address = response.addresses[0] ?? null;
      this.locationAddress$.set(response.addresses[0]);
      return address;
    } catch (error) {
      this.logService.error('[LocationService] Reverse Geocode Failed', { error });
      return null;
    }
  }

  async navigate(latitude: number, longitude: number, name?: string): Promise<void> {
    this.logService.info('[LocationService] navigate');
    try {
      await LaunchNavigator.navigate({
        destination: [latitude, longitude],
        options: {
          destinationName: name
        }
      });
    } catch (error) {
      this.logService.error('[LocationService] Failed to launch navigator', { error });
    }
  }

  async addResumeEventListener() {
    const listener = await App.addListener('resume', async () => {
      try {
        const location = await Geolocation.getCurrentPosition();
        this.location$.set(location);
      } catch (error) {
        this.logService.error('[LocationService] Location Permission Denied After Resume', { error });
      } finally {
        listener.remove();
      }
    });
  }
}
